<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-inspection-task-monitor">
    <nx-report-header title="巡检任务监控 · 异常处理看板">
      <div class="kb-header-actions">
        <span style="font-size: 12px; color: #64748b">数据刷新 {{ data.refreshTime || '—' }}</span>
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadData">刷新</el-button>
      </div>
    </nx-report-header>

    <el-form :inline="true" class="kb-filter-bar" size="small" @submit.native.prevent>
      <el-form-item label="业务日">
        <el-date-picker
          v-model="bizDate"
          type="date"
          value-format="yyyy-MM-dd"
          placeholder="选择日期"
          style="width: 160px"
        />
      </el-form-item>
      <el-form-item label="班组">
        <el-select v-model="teamName" clearable filterable placeholder="全部班组" style="width: 160px">
          <el-option v-for="t in data.teamOptions || []" :key="t" :label="t" :value="t" />
        </el-select>
      </el-form-item>
      <el-form-item label="工单状态">
        <el-select v-model="orderStatus" clearable placeholder="全部" style="width: 140px">
          <el-option v-for="o in statusOptions" :key="o.v" :label="o.l" :value="o.v" />
        </el-select>
      </el-form-item>
      <el-form-item label="路线">
        <el-select v-model="routeName" clearable filterable placeholder="全部路线" style="width: 180px">
          <el-option v-for="r in data.routeOptions || []" :key="r" :label="r" :value="r" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="loadData">应用筛选</el-button>
        <el-button @click="resetFilters">重置</el-button>
      </el-form-item>
    </el-form>

    <div class="kb-kpi-row-6">
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">今日工单总数</div>
        <div class="kb-kpi-value">{{ fmtInt(data.totalOrders) }}</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">待执行</div>
        <div class="kb-kpi-value">{{ fmtInt(data.pendingCount) }}</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">执行中</div>
        <div class="kb-kpi-value">{{ fmtInt(data.inProgressCount) }}</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">已完成</div>
        <div class="kb-kpi-value">{{ fmtInt(data.completedCount) }}</div>
      </div>
      <div class="kb-kpi-card is-warn">
        <div class="kb-kpi-label">超时 / 风险</div>
        <div class="kb-kpi-value">{{ fmtInt(data.riskCount) }}</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">点位完成率</div>
        <div class="kb-kpi-value">{{ fmtPctNum(data.pointCompletionRatePct) }}</div>
      </div>
    </div>

    <div class="kb-grid-3">
      <div class="kb-panel">
        <div class="kb-panel-head">工单状态分布</div>
        <div ref="chartStatusRef" class="kb-chart-h" />
      </div>
      <div class="kb-panel">
        <div class="kb-panel-head">今日按小时 · 完成工单数</div>
        <div ref="chartHourRef" class="kb-chart-h" />
      </div>
      <div class="kb-panel">
        <div class="kb-panel-head">班组 · 待执行 + 执行中</div>
        <div ref="chartTeamRef" class="kb-chart-h" />
      </div>
    </div>

    <div class="kb-panel" style="margin-bottom: 18px">
      <div class="kb-panel-head">
        <span class="kb-badge">异常督办</span>
      </div>
      <el-table :data="data.abnormalRows || []" border stripe size="small" style="width: 100%">
        <el-table-column label="工单号" min-width="140">
          <template slot-scope="{ row }">
            <router-link
              v-if="row.id"
              :to="'/dispatched-personnel-track/InspectionTaskManage/detail/' + row.id"
              class="el-link el-link--primary"
            >
              {{ row.workOrderId || row.id }}
            </router-link>
            <span v-else>{{ row.workOrderId || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="计划/路线" min-width="200" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ row.extPlanName || '—' }} / {{ row.routeName || '—' }}
          </template>
        </el-table-column>
        <el-table-column prop="teamName" label="班组" width="120" show-overflow-tooltip />
        <el-table-column label="进度" width="100" align="center">
          <template slot-scope="{ row }">
            {{ row.completedPointCount != null ? row.completedPointCount : 0 }} /
            {{ row.planPointCount != null ? row.planPointCount : 0 }}
          </template>
        </el-table-column>
        <el-table-column prop="severity" label="异常类型" min-width="160" show-overflow-tooltip />
        <el-table-column prop="suggestAction" label="建议动作" min-width="220" show-overflow-tooltip />
      </el-table>
    </div>

    <div class="kb-panel" style="margin-bottom: 18px">
      <div class="kb-panel-head">
        <span class="kb-badge-fence">围栏安全</span>
      </div>
      <el-form :inline="true" size="small" style="margin-bottom: 12px" @submit.native.prevent>
        <el-form-item label="闭环状态">
          <el-select v-model="fenceCycleFilter" style="width: 200px" @change="loadData">
            <el-option label="全部记录" value="" />
            <el-option label="区内未离场（leave_time 空）" value="open" />
            <el-option label="已离场闭环" value="closed" />
          </el-select>
        </el-form-item>
      </el-form>
      <div class="kb-fence-kpi-row">
        <div class="kb-kpi-card">
          <div class="kb-kpi-label">进出闭环记录数</div>
          <div class="kb-kpi-value">{{ fmtInt(fence.totalRecords) }}</div>
        </div>
        <div class="kb-kpi-card">
          <div class="kb-kpi-label">区内未离场</div>
          <div class="kb-kpi-value">{{ fmtInt(fence.openInsideCount) }}</div>
        </div>
        <div class="kb-kpi-card">
          <div class="kb-kpi-label">已发本人通知</div>
          <div class="kb-kpi-value" style="color: #15803d">{{ fmtInt(fence.selfNotifiedCount) }}</div>
        </div>
      </div>
      <div class="kb-grid-2" style="margin-bottom: 0">
        <div>
          <div class="kb-panel-head" style="font-size: 13px">按小时 · 进入危险区次数（enter_time）</div>
          <div ref="chartFenceHourRef" class="kb-chart-h" style="height: 240px" />
        </div>
        <div style="overflow-x: auto">
          <div class="kb-panel-head" style="font-size: 13px">围栏进出明细</div>
          <el-table :data="fence.rows || []" border stripe size="small" style="width: 100%; min-width: 720px">
            <el-table-column label="报警类型" min-width="120" align="center" show-overflow-tooltip>
              <template slot-scope="{ row }">
                <span>{{ fmtFenceAlarmTypeLabel(row) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="进入时间" width="155">
              <template slot-scope="{ row }">{{ fmtDt(row.enterTime) }}</template>
            </el-table-column>
            <el-table-column label="离开时间" width="155">
              <template slot-scope="{ row }">{{ fmtDt(row.leaveTime) }}</template>
            </el-table-column>
            <el-table-column label="停留(s)" width="80" align="right">
              <template slot-scope="{ row }">{{ row.stayDurationSec != null ? row.stayDurationSec : '—' }}</template>
            </el-table-column>
            <el-table-column prop="tagId" label="tagid" width="100" show-overflow-tooltip />
            <el-table-column prop="employeeName" label="员工" width="100" show-overflow-tooltip />
            <el-table-column prop="fenceName" label="围栏" min-width="120" show-overflow-tooltip />
            <el-table-column label="闭环" width="72" align="center">
              <template slot-scope="{ row }">{{ row.closed ? '已离场' : '区内' }}</template>
            </el-table-column>
            <el-table-column label="本人通知" width="88" align="center">
              <template slot-scope="{ row }">{{ row.selfNotified ? '是' : '否' }}</template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>

    <div class="kb-grid-2">
      <div class="kb-panel">
        <div class="kb-panel-head">路线维度 · 当日完成率 TOP</div>
        <div ref="chartRouteRef" class="kb-chart-route" />
      </div>
      <div class="kb-panel">
        <div class="kb-panel-head">异常处理动态（最近更新）</div>
        <div class="kb-timeline">
          <div v-for="(it, idx) in data.timeline || []" :key="idx" class="kb-tl-item">
            <div class="kb-tl-time">{{ it.timeText }}</div>
            <div class="kb-tl-title">{{ it.title }}</div>
            <div class="kb-tl-detail">{{ it.detail }}</div>
          </div>
          <el-empty v-if="!(data.timeline && data.timeline.length)" description="暂无动态" :image-size="64" />
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchInspectionTaskMonitor } from './api'
import './index.scss'

function todayStr() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export default {
  name: 'KanbanInspectionTaskMonitor',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      bizDate: todayStr(),
      teamName: '',
      orderStatus: null,
      routeName: '',
      fenceCycleFilter: '',
      statusOptions: [
        { v: 0, l: '待执行' },
        { v: 1, l: '执行中' },
        { v: 2, l: '已完成' },
        { v: 3, l: '已超时' },
        { v: 4, l: '超时完成' },
        { v: 5, l: '已取消' },
        { v: 6, l: '未执行' }
      ],
      data: {
        teamOptions: [],
        routeOptions: [],
        fence: {}
      },
      chartStatus: null,
      chartHour: null,
      chartTeam: null,
      chartFenceHour: null,
      chartRoute: null
    }
  },
  computed: {
    fence() {
      return this.data.fence || {}
    }
  },
  watch: {
    data: {
      handler() {
        this.$nextTick(() => this.renderAllCharts())
      },
      deep: true
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    this.disposeCharts()
  },
  methods: {
    fmtInt(v) {
      if (v == null) return '0'
      return String(v)
    },
    /** alarm_type 整型字典 → 中文（与 produce WeiduGeofenceAlarmServiceImpl 一致）；兼容 alarmType / alarm_type */
    fmtFenceAlarmTypeLabel(row) {
      if (!row) return '—'
      const v = row.alarmType != null ? row.alarmType : row.alarm_type
      if (v === null || v === undefined || v === '') return '—'
      const n = Number(v)
      if (Number.isNaN(n)) return String(v)
      const map = {
        1: '电子围栏（危险区）'
      }
      if (map[n]) return map[n]
      return `其它类型（编码 ${n}）`
    },
    fmtPctNum(v) {
      if (v == null) return '0%'
      const n = typeof v === 'number' ? v : parseFloat(v)
      if (Number.isNaN(n)) return '0%'
      return `${n.toFixed(1)}%`
    },
    fmtDt(v) {
      if (v == null || v === '') return '—'
      if (typeof v === 'number') {
        const d = new Date(v)
        if (Number.isNaN(d.getTime())) return '—'
        const pad = (n) => String(n).padStart(2, '0')
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
      }
      if (typeof v === 'string') return v.length >= 19 ? v.slice(0, 19) : v
      return '—'
    },
    resetFilters() {
      this.bizDate = todayStr()
      this.teamName = ''
      this.orderStatus = null
      this.routeName = ''
      this.fenceCycleFilter = ''
      this.loadData()
    },
    async loadData() {
      this.loading = true
      try {
        const params = {
          bizDate: this.bizDate || todayStr(),
          teamName: this.teamName || undefined,
          orderStatus: this.orderStatus,
          routeName: this.routeName || undefined,
          fenceCycleFilter: this.fenceCycleFilter || undefined
        }
        const res = await fetchInspectionTaskMonitor(params)
        this.data = res || {}
        this.$nextTick(() => this.renderAllCharts())
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    onResize() {
      [this.chartStatus, this.chartHour, this.chartTeam, this.chartFenceHour, this.chartRoute].forEach((c) => c && c.resize())
    },
    disposeCharts() {
      [this.chartStatus, this.chartHour, this.chartTeam, this.chartFenceHour, this.chartRoute].forEach((c) => {
        if (c) c.dispose()
      })
      this.chartStatus = null
      this.chartHour = null
      this.chartTeam = null
      this.chartFenceHour = null
      this.chartRoute = null
    },
    renderAllCharts() {
      this.renderPie()
      this.renderHour()
      this.renderTeam()
      this.renderFenceHour()
      this.renderRoute()
    },
    renderPie() {
      const el = this.$refs.chartStatusRef
      if (!el) return
      const pie = this.data.statusPie || []
      if (!this.chartStatus) this.chartStatus = echarts.init(el)
      this.chartStatus.setOption({
        tooltip: { trigger: 'item' },
        legend: { bottom: 0, type: 'scroll' },
        series: [
          {
            type: 'pie',
            radius: ['36%', '62%'],
            data: pie.map((p) => ({ name: p.name, value: p.value }))
          }
        ]
      })
    },
    renderHour() {
      const el = this.$refs.chartHourRef
      if (!el) return
      const hours = this.data.hourCompletedCounts || []
      const x = Array.from({ length: 24 }, (_, i) => `${i}时`)
      if (!this.chartHour) this.chartHour = echarts.init(el)
      this.chartHour.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: 40, right: 16, top: 24, bottom: 32 },
        xAxis: { type: 'category', data: x },
        yAxis: { type: 'value', minInterval: 1 },
        series: [{ type: 'bar', data: hours, itemStyle: { color: '#ea580c' } }]
      })
    },
    renderTeam() {
      const el = this.$refs.chartTeamRef
      if (!el) return
      const bars = this.data.teamActiveBars || []
      if (!this.chartTeam) this.chartTeam = echarts.init(el)
      this.chartTeam.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: 100, right: 16, top: 16, bottom: 24 },
        xAxis: { type: 'value', minInterval: 1 },
        yAxis: { type: 'category', data: bars.map((b) => b.name).reverse() },
        series: [
          {
            type: 'bar',
            data: bars.map((b) => b.value).reverse(),
            itemStyle: { color: '#2563eb' }
          }
        ]
      })
    },
    renderFenceHour() {
      const el = this.$refs.chartFenceHourRef
      if (!el) return
      const hours = this.fence.hourEnterCounts || []
      const x = Array.from({ length: 24 }, (_, i) => `${i}时`)
      if (!this.chartFenceHour) this.chartFenceHour = echarts.init(el)
      this.chartFenceHour.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: 40, right: 16, top: 24, bottom: 32 },
        xAxis: { type: 'category', data: x },
        yAxis: { type: 'value', minInterval: 1 },
        series: [{ type: 'line', smooth: true, data: hours, areaStyle: { opacity: 0.12 }, itemStyle: { color: '#1d4ed8' } }]
      })
    },
    renderRoute() {
      const el = this.$refs.chartRouteRef
      if (!el) return
      const rr = this.data.routeRates || []
      if (!this.chartRoute) this.chartRoute = echarts.init(el)
      this.chartRoute.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: 120, right: 16, top: 16, bottom: 24 },
        xAxis: { type: 'value', max: 100 },
        yAxis: {
          type: 'category',
          data: rr.map((r) => (r.routeName || '').slice(0, 14)).reverse()
        },
        series: [
          {
            type: 'bar',
            data: rr.map((r) => (r.ratePct != null ? Number(r.ratePct) : 0)).reverse(),
            itemStyle: { color: '#0d9488' }
          }
        ]
      })
    }
  }
}
</script>
